import React from "react";
import RoommateView from "./RoommateView";
import { Accordion, Card, Col, Row, Badge } from "react-bootstrap";
import moment from "moment";
import { connect } from "react-redux";
import { SortProvider } from "../services/SortingProvider";
import DateRangeRenderer from "./renderers/DateRangeRenderer";

interface AddressViewProps
{
    data: Address[];
    addressType: string;
    role?: any;
    settings?: any;
}

interface AddressViewStates
{
    openCurrent: boolean;
    openHistoric: boolean;
}

// data now contains this addressType's full current+historic history in one array
// (a single /student/addresses call, no separate historic request) - current vs
// historic is worked out here, locally, by comparing each address's from/thru dates
// against today, rather than trusting any current/historic split from the caller.
// Historic stays behind a collapsed-by-default accordion like before, just backed by
// data that's already loaded rather than a fresh request on expand.
class AddressView extends React.Component<AddressViewProps, AddressViewStates>
{
    sortProvider: SortProvider;

    constructor(props: AddressViewProps)
    {
        super(props);
        this.sortProvider = new SortProvider();

        this.state = {
            openCurrent: true,
            openHistoric: false
        };
    }

    render(): React.ReactNode
    {
        const today = moment();
        // SortProvider.sort mutates in place - clone first so this.props.data isn't touched.
        const sorted: Address[] = this.sortProvider.sort([...(this.props.data || [])], "Desc");
        const current = sorted.find((itm) => !itm.thruDate || (moment(itm.thruDate) >= today && moment(itm.fromDate) <= today));
        const historic = sorted.filter((itm) => itm !== current);

        let privacyClass = "";

        if (this.props.settings.length > 0)
            privacyClass = (this.props.role == "academicAdvisor" && this.props.settings.filter(itm => itm.settingId == "psContactRoommates")[0].isActive == true) ? "d-none" : "";

        if (current)
        {
            if (current.street == null && current.city == null && current.state == null && current.country == null)
            {
                return <div className="text-center" id='error'>
                    <br />
                    <p className="error-text">No Addresses available</p>
                </div>;
            }
        }

        return <React.Fragment>
            <Accordion defaultActiveKey={"0"}>
                <Card>
                    <Accordion.Toggle href={"javascript:void(0)"} as={"a"} variant="link" eventKey="0" aria-expanded={this.state.openCurrent} onClick={() => this.setState({ openCurrent: !this.state.openCurrent })}>
                        <Card.Header>
                            <Row>
                                <Col xs={10}>
                                    <h3>
                                        <b>Current Address Information &nbsp;</b>
                                    </h3>
                                </Col>
                                <Col xs={1} className={"chevron"}>
                                    <h3><i className={this.state.openCurrent ? " far fa-chevron-up" : "far fa-chevron-down"}></i></h3>
                                </Col>
                            </Row>
                        </Card.Header>
                    </Accordion.Toggle>
                    <Accordion.Collapse eventKey="0">
                        <Card.Body>
                            {
                                current
                                    ?
                                    <React.Fragment>
                                        <div className="darker-text">
                                            <Row>
                                                <Col md={6}>
                                                    <Row>
                                                        <Col>{current.street}</Col>
                                                    </Row>
                                                    <Row>
                                                        <Col>{current.city}</Col>
                                                    </Row>
                                                    <Row>
                                                        <Col>{current.state}</Col>
                                                    </Row>
                                                    <Row>
                                                        <Col>{current.country}</Col>
                                                    </Row>
                                                </Col>
                                                <Col md={6}>
                                                    <DateRangeRenderer fromDate={current.fromDate} thruDate={current.thruDate} />
                                                </Col>
                                            </Row>
                                        </div>
                                        {
                                            <div className={privacyClass + " mt-3 darker-text"}>
                                                {
                                                    (current.roomMates && current.roomMates.length > 0)
                                                    &&
                                                    <Row className={"mb-3"}>
                                                        <Col md="auto" className={"grayText"}>Current Roommates</Col>
                                                        <Col md="auto">
                                                            {
                                                                current.roomMates.map((itm: RoomMate, i: number) =>
                                                                {
                                                                    return <RoommateView data={itm} key={i} />;
                                                                })
                                                            }
                                                        </Col>
                                                    </Row>
                                                }
                                            </div>
                                        }
                                    </React.Fragment>
                                    :
                                    <div className="text-center" id='error'>
                                        <br />
                                        <p className="error-text">No Current Addresses data available</p>
                                    </div>
                            }
                        </Card.Body>
                    </Accordion.Collapse>
                </Card>
            </Accordion>
            <Accordion className={"top-border"}>
                <Card>
                    <Accordion.Toggle href={"javascript:void(0)"} as={"a"} variant="link" eventKey="0" aria-expanded={this.state.openHistoric} onClick={() => this.setState({ openHistoric: !this.state.openHistoric })}>
                        <Card.Header>
                            <Row>
                                <Col xs={10}>
                                    <h3>
                                        <b>Historical Address Information &nbsp;
                                            <Badge pill style={{ opacity: "0.5" }} variant="secondary">{historic.length}</Badge>
                                        </b>
                                    </h3>
                                </Col>
                                <Col xs={1} className={"chevron"}>
                                    <h3><i className={this.state.openHistoric ? " far fa-chevron-up" : "far fa-chevron-down"}></i></h3>
                                </Col>
                            </Row>
                        </Card.Header>
                    </Accordion.Toggle>
                    <Accordion.Collapse eventKey="0">
                        <Card.Body>
                            {
                                historic.length > 0
                                    ?
                                    historic.map((itm, i) =>
                                    {
                                        return <div className={"darker-text address-item"} key={i}>
                                            <Row>
                                                <Col md={6}>
                                                    <Row>
                                                        <Col>{itm.street}</Col>
                                                    </Row>
                                                    <Row>
                                                        <Col>{itm.city}</Col>
                                                    </Row>
                                                    <Row>
                                                        <Col>{itm.state}</Col>
                                                    </Row>
                                                    <Row>
                                                        <Col>{itm.country}</Col>
                                                    </Row>
                                                </Col>
                                                <Col md={6}>
                                                    <DateRangeRenderer fromDate={itm.fromDate} thruDate={itm.thruDate} />
                                                </Col>
                                            </Row>
                                            {
                                                (itm.roomMates && itm.roomMates.length > 0)
                                                &&
                                                <Row className={privacyClass + " mb-3 mt-3"}>
                                                    <Col md="auto" className={"grayText"}>Previous Roommates</Col>
                                                    <Col md="auto">
                                                        {
                                                            itm.roomMates.map((person: RoomMate, k: number) =>
                                                            {
                                                                return <RoommateView data={person} key={k} />;
                                                            })
                                                        }
                                                    </Col>
                                                </Row>
                                            }
                                        </div>;
                                    })
                                    :
                                    <div className="text-center" id='error'>
                                        <br />
                                        <p className="error-text">No Addresses available</p>
                                    </div>
                            }
                        </Card.Body>
                    </Accordion.Collapse>
                </Card>
            </Accordion>
        </React.Fragment>;
    }
}

const mapStateToProps = (state: any) => ({
    role: state.app.user.role,
    settings: state.app.privacySettings
});

export default connect(mapStateToProps)(AddressView);
